import { Component } from '@theme/component';

const FILTER_FORM_SELECTOR = '[data-atc-media-filter-form]';
const FILTER_SELECTOR = '[data-atc-media-filter]';
const FILTER_PILL_SELECTOR = '[data-atc-media-gallery-filter-pill]';
const CLEAR_FILTERS_SELECTOR = '[data-atc-media-clear-filters]';
const LOAD_MORE_SELECTOR = '[data-atc-media-load-more]';
const RESULTS_SELECTOR = '[data-atc-media-results]';
const ITEM_TEMPLATE_SELECTOR = '[data-atc-media-gallery-item]';
const EMPTY_SELECTOR = '.atc-media-gallery__empty';
const STATUS_SELECTOR = '[data-atc-media-status]';
const COUNT_BADGE_SELECTOR = '[data-atc-media-count-badge]';
const ISSUE_SELECTOR = '[data-atc-media-gallery-issue]';
const FACET_BUBBLE_SELECTOR = '.facets__status-wrapper .hide-when-empty';

const PARAM_MUSIC_RELEASE = 'media_music_release';
const PARAM_EVENT = 'media_event';
const PARAM_CATEGORY = 'media_category';
const PARAM_MEDIA_TYPE = 'media_type';
const PARAM_COUNT = 'media_count';

/**
 * ATC overlay: Replaces fetch/section-morph filtering with pure client-side DOM
 * management. All media items in the merchant's scope are pre-rendered in inert
 * <template> elements by Liquid; JavaScript reads URL params, filters matching
 * templates, and builds the grid (standard or editorial) without any network
 * requests.
 *
 * @typedef {object} Refs
 * @extends {Component<Refs>}
 */
class AtcMediaGallery extends Component {
  /** @type {Set<string>} */
  #reportedIssues = new Set();

  /** @type {number | null} */
  #pendingRaf = null;

  /** @type {HTMLTemplateElement[]} */
  #itemTemplates = [];

  connectedCallback() {
    super.connectedCallback();
    this.#itemTemplates = Array.from(this.querySelectorAll(ITEM_TEMPLATE_SELECTOR)).filter(
      (el) => el instanceof HTMLTemplateElement
    );
    this.addEventListener('change', this.#handleChange);
    this.addEventListener('submit', this.#handleSubmit);
    this.addEventListener('click', this.#handleClick);
    window.addEventListener('popstate', this.#handlePopState);
    this.#syncFromUrl();
    this.#reportIssues();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#itemTemplates = [];
    this.removeEventListener('change', this.#handleChange);
    this.removeEventListener('submit', this.#handleSubmit);
    this.removeEventListener('click', this.#handleClick);
    window.removeEventListener('popstate', this.#handlePopState);

    if (this.#pendingRaf !== null) {
      cancelAnimationFrame(this.#pendingRaf);
      this.#pendingRaf = null;
    }
  }

  updatedCallback() {
    super.updatedCallback();
    this.#reportIssues();
  }

  get sectionId() {
    return this.dataset.sectionId || '';
  }

  get itemsPerLoad() {
    return Number(this.dataset.itemsPerLoad) || 1;
  }

  get maxItems() {
    return Number(this.dataset.maxItems) || 0;
  }

  get limitItems() {
    return this.dataset.limitItems === 'true';
  }

  get useEditorialGrid() {
    return this.dataset.useEditorialGrid === 'true';
  }

  /** @param {Event} event */
  #handleChange = (event) => {
    if (!(event.target instanceof Element)) return;
    if (!event.target.matches(FILTER_SELECTOR)) return;

    const form = event.target.closest(FILTER_FORM_SELECTOR);
    if (!(form instanceof HTMLFormElement)) return;

    this.#navigate(this.#buildFilterUrl(form));
  };

  /** @param {SubmitEvent} event */
  #handleSubmit = (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.matches(FILTER_FORM_SELECTOR)) return;

    event.preventDefault();
    this.#navigate(this.#buildFilterUrl(form));
  };

  /** @param {MouseEvent} event */
  #handleClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const clearFilters = event.target.closest(CLEAR_FILTERS_SELECTOR);

    if (clearFilters instanceof HTMLElement && this.contains(clearFilters)) {
      event.preventDefault();
      this.#navigate(this.#buildClearFiltersUrl());
      return;
    }

    const pill = event.target.closest(FILTER_PILL_SELECTOR);

    if (pill instanceof HTMLElement && this.contains(pill)) {
      event.preventDefault();
      const param = pill.dataset.filterParam || '';
      const value = pill.dataset.filterValue || '';
      const url = new URL(window.location.href);

      if (value) {
        url.searchParams.set(param, value);
      } else {
        url.searchParams.delete(param);
      }

      this.#setCountParam(url, this.itemsPerLoad);
      this.#navigate(url);
      return;
    }

    const loadMore = event.target.closest(LOAD_MORE_SELECTOR);
    if (!(loadMore instanceof HTMLElement) || !this.contains(loadMore)) return;

    event.preventDefault();
    const url = new URL(window.location.href);
    const nextCount = Number(loadMore.dataset.nextCount);

    if (nextCount > 0) {
      this.#setCountParam(url, nextCount);
    }

    this.#navigate(url, { replace: true });
  };

  #handlePopState = () => {
    this.#syncFromUrl();
  };

  /**
   * @param {HTMLFormElement} form
   * @returns {URL}
   */
  #buildFilterUrl(form) {
    const formData = new FormData(form);
    const url = new URL(window.location.href);
    const musicReleaseParam = this.#getParamName(PARAM_MUSIC_RELEASE);
    const eventParam = this.#getParamName(PARAM_EVENT);
    const categoryParam = this.#getParamName(PARAM_CATEGORY);
    const mediaTypeParam = this.#getParamName(PARAM_MEDIA_TYPE);

    url.searchParams.delete(musicReleaseParam);
    url.searchParams.delete(eventParam);
    url.searchParams.delete(categoryParam);
    url.searchParams.delete(mediaTypeParam);
    this.#setCountParam(url, this.itemsPerLoad);

    const musicReleaseFilters = formData.getAll(musicReleaseParam).map(String).filter(Boolean);
    const eventFilters = formData.getAll(eventParam).map(String).filter(Boolean);
    const categoryFilters = formData.getAll(categoryParam).map(String).filter(Boolean);
    const mediaTypeFilters = formData.getAll(mediaTypeParam).map(String).filter(Boolean);

    if (musicReleaseFilters.length > 0) {
      url.searchParams.set(musicReleaseParam, musicReleaseFilters.join(','));
    }

    if (eventFilters.length > 0) {
      url.searchParams.set(eventParam, eventFilters.join(','));
    }

    if (categoryFilters.length > 0) {
      url.searchParams.set(categoryParam, categoryFilters.join(','));
    }

    if (mediaTypeFilters.length > 0) {
      url.searchParams.set(mediaTypeParam, mediaTypeFilters.join(','));
    }

    return url;
  }

  /** @returns {URL} */
  #buildClearFiltersUrl() {
    const url = new URL(window.location.href);

    url.searchParams.delete(this.#getParamName(PARAM_MUSIC_RELEASE));
    url.searchParams.delete(this.#getParamName(PARAM_EVENT));
    url.searchParams.delete(this.#getParamName(PARAM_CATEGORY));
    url.searchParams.delete(this.#getParamName(PARAM_MEDIA_TYPE));
    this.#setCountParam(url, this.itemsPerLoad);

    return url;
  }

  /**
   * Sets the count URL param, omitting it when the value equals the default
   * items-per-load so that default-state URLs stay clean.
   *
   * @param {URL} url
   * @param {number} count
   */
  #setCountParam(url, count) {
    const countParam = this.#getParamName(PARAM_COUNT);

    if (count === this.itemsPerLoad) {
      url.searchParams.delete(countParam);
    } else {
      url.searchParams.set(countParam, String(count));
    }
  }

  /**
   * @param {URL} url
   * @param {{ replace?: boolean }} [options]
   */
  #navigate(url, { replace = false } = {}) {
    if (replace) {
      history.replaceState({ mediaGallery: true }, '', url.toString());
    } else {
      history.pushState({ mediaGallery: true }, '', url.toString());
    }

    this.#scheduleSyncFromUrl();
  }

  #scheduleSyncFromUrl() {
    if (this.#pendingRaf !== null) {
      cancelAnimationFrame(this.#pendingRaf);
    }

    this.#pendingRaf = requestAnimationFrame(() => {
      this.#pendingRaf = null;
      this.#syncFromUrl();
    });
  }

  #syncFromUrl() {
    const url = new URL(window.location.href);
    const musicReleaseValues = this.#parseFilterParam(
      url.searchParams.get(this.#getParamName(PARAM_MUSIC_RELEASE))
    );
    const eventValues = this.#parseFilterParam(
      url.searchParams.get(this.#getParamName(PARAM_EVENT))
    );
    const categoryValues = this.#parseFilterParam(
      url.searchParams.get(this.#getParamName(PARAM_CATEGORY))
    );
    const mediaTypeValues = this.#parseFilterParam(
      url.searchParams.get(this.#getParamName(PARAM_MEDIA_TYPE))
    );
    const requestedCount =
      Number(url.searchParams.get(this.#getParamName(PARAM_COUNT))) || this.itemsPerLoad;

    this.#syncCheckboxes(this.#getParamName(PARAM_MUSIC_RELEASE), musicReleaseValues);
    this.#syncCheckboxes(this.#getParamName(PARAM_EVENT), eventValues);
    this.#syncCheckboxes(this.#getParamName(PARAM_CATEGORY), categoryValues);
    this.#syncCheckboxes(this.#getParamName(PARAM_MEDIA_TYPE), mediaTypeValues);
    this.#syncPills(url);

    this.#updateGrid(musicReleaseValues, eventValues, categoryValues, mediaTypeValues, requestedCount);
    this.#updateFacetBubbles();

    const totalActive =
      musicReleaseValues.length + eventValues.length + categoryValues.length + mediaTypeValues.length;
    this.#updateFilterUI(totalActive);
  }

  /**
   * @param {string | null} value
   * @returns {string[]}
   */
  #parseFilterParam(value) {
    if (!value) return [];

    return value
      .split(',')
      .map((v) => v.trim())
      .filter(Boolean);
  }

  /**
   * @param {string} paramName
   * @param {string[]} activeValues
   */
  #syncCheckboxes(paramName, activeValues) {
    const activeSet = new Set(activeValues);

    for (const input of this.querySelectorAll(FILTER_SELECTOR)) {
      if (!(input instanceof HTMLInputElement) || input.name !== paramName) continue;

      input.checked = activeSet.has(input.value);
      const item = input.closest('.facets__inputs-list-item');

      if (item instanceof HTMLElement) {
        item.classList.toggle('facets__inputs-list-item--active', input.checked);
      }
    }
  }

  /** @param {URL} url */
  #syncPills(url) {
    for (const pill of this.querySelectorAll(FILTER_PILL_SELECTOR)) {
      if (!(pill instanceof HTMLElement)) continue;

      const param = pill.dataset.filterParam || '';
      const value = pill.dataset.filterValue || '';
      const currentValue = url.searchParams.get(param) || '';
      const isActive = currentValue === value;

      pill.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      pill.classList.toggle('atc-media-gallery__filter-pill--active', isActive);
    }
  }

  /**
   * @param {string[]} musicReleaseValues
   * @param {string[]} eventValues
   * @param {string[]} categoryValues
   * @param {string[]} mediaTypeValues
   * @param {number} requestedCount
   */
  #updateGrid(musicReleaseValues, eventValues, categoryValues, mediaTypeValues, requestedCount) {
    const results = this.querySelector(RESULTS_SELECTOR);
    const emptyEl = this.querySelector(EMPTY_SELECTOR);
    if (!(results instanceof HTMLElement)) return;

    const musicReleaseSet = new Set(musicReleaseValues);
    const eventSet = new Set(eventValues);
    const categorySet = new Set(categoryValues);
    const mediaTypeSet = new Set(mediaTypeValues);
    const hasFilters =
      musicReleaseSet.size > 0 ||
      eventSet.size > 0 ||
      categorySet.size > 0 ||
      mediaTypeSet.size > 0;

    /** @type {HTMLTemplateElement[]} */
    const matched = [];

    for (const template of this.#itemTemplates) {
      if (this.#matchesFilters(template, musicReleaseSet, eventSet, categorySet, mediaTypeSet)) {
        matched.push(template);
      }
    }

    let visibleCount = requestedCount;

    if (this.limitItems && this.maxItems > 0 && visibleCount > this.maxItems) {
      visibleCount = this.maxItems;
    }

    if (visibleCount > matched.length) {
      visibleCount = matched.length;
    }

    if (this.useEditorialGrid) {
      this.#buildEditorialGrid(results, matched, visibleCount);
    } else {
      this.#buildStandardGrid(results, matched, visibleCount);
    }

    if (emptyEl instanceof HTMLElement) {
      if (visibleCount === 0) {
        emptyEl.textContent = hasFilters
          ? 'No media items are available for the selected filters.'
          : 'No media items are available.';
        emptyEl.removeAttribute('hidden');
      } else {
        emptyEl.setAttribute('hidden', '');
      }
    }

    const nextCount = this.#computeNextVisibleCount(visibleCount, matched.length);
    this.#updateLoadMore(nextCount > visibleCount, nextCount);
    this.#updateStatus(matched.length);
  }

  /**
   * @param {HTMLTemplateElement} template
   * @param {Set<string>} musicReleaseSet
   * @param {Set<string>} eventSet
   * @param {Set<string>} categorySet
   * @param {Set<string>} mediaTypeSet
   * @returns {boolean}
   */
  #matchesFilters(template, musicReleaseSet, eventSet, categorySet, mediaTypeSet) {
    if (musicReleaseSet.size > 0) {
      const ids = (template.dataset.musicReleaseIds || '').split(' ').filter(Boolean);

      if (!ids.some((id) => musicReleaseSet.has(id))) return false;
    }

    if (eventSet.size > 0) {
      if (!eventSet.has(template.dataset.eventId || '')) return false;
    }

    if (categorySet.size > 0) {
      const ids = (template.dataset.categoryIds || '').split(' ').filter(Boolean);

      if (!ids.some((id) => categorySet.has(id))) return false;
    }

    if (mediaTypeSet.size > 0) {
      if (!mediaTypeSet.has(template.dataset.mediaType || '')) return false;
    }

    return true;
  }

  /**
   * @param {HTMLElement} results
   * @param {HTMLTemplateElement[]} templates
   * @param {number} count
   */
  #buildStandardGrid(results, templates, count) {
    results.innerHTML = '';

    for (let i = 0; i < count; i++) {
      const item = document.createElement('div');
      item.className = 'atc-media-gallery__item';
      item.appendChild(templates[i].content.cloneNode(true));
      results.appendChild(item);
    }
  }

  /**
   * Builds the editorial A grid layout. Items are grouped into modules of 5:
   * one feature item followed by two rows of two items each. Odd-indexed modules
   * flip the feature to the right.
   *
   * @param {HTMLElement} results
   * @param {HTMLTemplateElement[]} templates
   * @param {number} count
   */
  #buildEditorialGrid(results, templates, count) {
    results.innerHTML = '';

    let i = 0;
    let moduleIndex = 0;

    while (i < count) {
      const moduleEl = document.createElement('div');
      moduleEl.className = 'atc-media-gallery__editorial-module';

      if (moduleIndex % 2 === 1) {
        moduleEl.classList.add('atc-media-gallery__editorial-module--feature-right');
      }

      const featureEl = document.createElement('div');
      featureEl.className = 'atc-media-gallery__editorial-feature';
      featureEl.appendChild(templates[i].content.cloneNode(true));
      moduleEl.appendChild(featureEl);
      i++;

      const stackEl = document.createElement('div');
      stackEl.className = 'atc-media-gallery__editorial-stack';
      moduleEl.appendChild(stackEl);

      if (i < count) {
        const row1El = document.createElement('div');
        row1El.className = 'atc-media-gallery__editorial-row';

        for (let r = 0; r < 2 && i < count; r++, i++) {
          const itemEl = document.createElement('div');
          itemEl.className = 'atc-media-gallery__editorial-item';
          itemEl.appendChild(templates[i].content.cloneNode(true));
          row1El.appendChild(itemEl);
        }

        stackEl.appendChild(row1El);
      }

      if (i < count) {
        const row2El = document.createElement('div');
        row2El.className = 'atc-media-gallery__editorial-row';

        for (let r = 0; r < 2 && i < count; r++, i++) {
          const itemEl = document.createElement('div');
          itemEl.className = 'atc-media-gallery__editorial-item';
          itemEl.appendChild(templates[i].content.cloneNode(true));
          row2El.appendChild(itemEl);
        }

        stackEl.appendChild(row2El);
      }

      results.appendChild(moduleEl);
      moduleIndex++;
    }
  }

  /**
   * @param {number} currentCount
   * @param {number} totalMatched
   * @returns {number}
   */
  #computeNextVisibleCount(currentCount, totalMatched) {
    let next = currentCount + this.itemsPerLoad;

    if (this.limitItems && this.maxItems > 0 && next > this.maxItems) {
      next = this.maxItems;
    }

    if (next > totalMatched) {
      next = totalMatched;
    }

    return next;
  }

  /**
   * @param {boolean} hasMore
   * @param {number} nextCount
   */
  #updateLoadMore(hasMore, nextCount) {
    const trigger = this.querySelector(LOAD_MORE_SELECTOR);
    if (!(trigger instanceof HTMLElement)) return;

    trigger.dataset.nextCount = String(nextCount);

    if (hasMore) {
      trigger.removeAttribute('hidden');
    } else {
      trigger.setAttribute('hidden', '');
    }
  }

  /** @param {number} matchedCount */
  #updateStatus(matchedCount) {
    let text = '';

    if (matchedCount === 0) {
      text = 'No media items are available for the selected filters.';
    } else if (matchedCount === 1) {
      text = '1 item selected.';
    } else {
      text = `${matchedCount} items selected.`;
    }

    for (const statusEl of this.querySelectorAll(STATUS_SELECTOR)) {
      statusEl.textContent = text;
    }
  }

  #updateFacetBubbles() {
    for (const details of this.querySelectorAll('details')) {
      if (!(details instanceof HTMLElement)) continue;

      const checkedCount = details.querySelectorAll(`${FILTER_SELECTOR}:checked`).length;
      const bubble = details.querySelector(FACET_BUBBLE_SELECTOR);
      if (!(bubble instanceof HTMLElement)) continue;

      bubble.textContent = checkedCount > 0 ? String(checkedCount) : '';

      if (checkedCount > 0) {
        bubble.classList.add('bubble', 'facets__bubble');
      } else {
        bubble.classList.remove('bubble', 'facets__bubble');
      }
    }
  }

  /** @param {number} activeCount */
  #updateFilterUI(activeCount) {
    for (const btn of this.querySelectorAll(CLEAR_FILTERS_SELECTOR)) {
      if (!(btn instanceof HTMLElement)) continue;

      if (activeCount > 0) {
        btn.removeAttribute('hidden');
      } else {
        btn.setAttribute('hidden', '');
      }
    }

    for (const badge of this.querySelectorAll(COUNT_BADGE_SELECTOR)) {
      if (!(badge instanceof HTMLElement)) continue;

      if (activeCount > 0) {
        badge.removeAttribute('hidden');

        if (badge.classList.contains('filter-count-bubble')) {
          badge.setAttribute('aria-label', `${activeCount} active filters`);
          const text = badge.querySelector('.filter-count-bubble__text');
          if (text instanceof HTMLElement) text.textContent = String(activeCount);
        } else {
          badge.textContent = String(activeCount);
        }
      } else {
        badge.setAttribute('hidden', '');
      }
    }
  }

  #reportIssues() {
    for (const issue of this.querySelectorAll(ISSUE_SELECTOR)) {
      const code = issue.getAttribute('data-issue-code') || 'invalid_configuration';
      const kind = issue.getAttribute('data-issue-kind') || 'unknown';
      const reference = issue.getAttribute('data-issue-reference') || 'unknown';
      this.#warnOnce(`${code}:${kind}:${reference}`, code, kind, reference);
    }
  }

  /**
   * @param {string} key
   * @param {string} code
   * @param {string} kind
   * @param {string} reference
   */
  #warnOnce(key, code, kind, reference) {
    if (this.#reportedIssues.has(key)) return;

    this.#reportedIssues.add(key);
    console.warn('[atc-media-gallery] Invalid gallery data was ignored.', {
      sectionId: this.sectionId,
      code,
      kind,
      reference,
    });
  }

  /**
   * @param {string} param
   * @returns {string}
   */
  #getParamName(param) {
    return `${param}_${this.sectionId}`;
  }
}

if (!customElements.get('atc-media-gallery')) {
  customElements.define('atc-media-gallery', AtcMediaGallery);
}
